/**
 * Country Data Service
 *
 * Fetches and caches country/currency data from the backend API.
 * Provides helper functions for currency lookups.
 *
 * This replaces the hardcoded COUNTRY_CURRENCY_MAP, COUNTRY_FLAG_MAP,
 * and CURRENCY_SYMBOLS constants that were duplicated across the codebase.
 *
 * @module features/country/api/countryData
 */

import { api } from '@/shared/lib/api';
import type { SupportedCountryItem } from '@/shared/lib/api';

// ============================================================================
// TYPES
// ============================================================================

interface CountryDataCache {
  countries: SupportedCountryItem[];
  countryCurrencyMap: Map<string, string>;
  currencySymbolMap: Map<string, string>;
  countryFlagMap: Map<string, string>;
  loadedAt: number | null;
}

// ============================================================================
// FALLBACK DATA (used when API is unavailable)
// ============================================================================

const FALLBACK_COUNTRY_CURRENCY: Record<string, string> = {
  US: 'USD',
  GB: 'GBP',
  DE: 'EUR',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  FI: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  NL: 'EUR',
  AT: 'EUR',
  BE: 'EUR',
  CH: 'CHF',
  CA: 'CAD',
  AU: 'AUD',
  NZ: 'NZD',
  JP: 'JPY',
};

const FALLBACK_CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  CHF: 'CHF',
  CAD: 'CA$',
  AUD: 'A$',
  NZD: 'NZ$',
  JPY: '¥',
};

const FALLBACK_COUNTRY_FLAG: Record<string, string> = {
  SE: '🇸🇪',
  NO: '🇳🇴',
  DK: '🇩🇰',
  FI: '🇫🇮',
  US: '🇺🇸',
  GB: '🇬🇧',
  DE: '🇩🇪',
  FR: '🇫🇷',
  IT: '🇮🇹',
  ES: '🇪🇸',
  NL: '🇳🇱',
  AT: '🇦🇹',
  BE: '🇧🇪',
  CH: '🇨🇭',
  CA: '🇨🇦',
  AU: '🇦🇺',
  NZ: '🇳🇿',
  JP: '🇯🇵',
};

// ============================================================================
// CACHE SINGLETON
// ============================================================================

const cache: CountryDataCache = {
  countries: [],
  countryCurrencyMap: new Map(),
  currencySymbolMap: new Map(),
  countryFlagMap: new Map(),
  loadedAt: null,
};

let fetchPromise: Promise<void> | null = null;

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Fetch countries from API and populate cache.
 * Safe to call multiple times - will only fetch once.
 */
export async function fetchCountries(): Promise<void> {
  // Already loaded
  if (cache.loadedAt !== null) {
    return;
  }

  // Already fetching
  if (fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = (async () => {
    try {
      const response = await api.getSupportedCountries();

      if (response.success) {
        cache.countries = response.countries;
        cache.loadedAt = Date.now();

        // Build lookup maps
        for (const country of response.countries) {
          if (country.currency_code) {
            cache.countryCurrencyMap.set(country.country_code, country.currency_code);
          }
          if (country.currency_symbol) {
            cache.currencySymbolMap.set(country.currency_code ?? '', country.currency_symbol);
          }
          // Flag emoji from Unicode regional indicators
          const flag = countryCodeToFlag(country.country_code);
          cache.countryFlagMap.set(country.country_code, flag);
        }
      }
    } catch {
      // Cache stays empty, fallback will be used
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

/**
 * Convert country code to flag emoji using Unicode regional indicators.
 */
function countryCodeToFlag(countryCode: string): string {
  const codePoints = [...countryCode.toUpperCase()].map(
    (char) => 0x1f1e6 + char.charCodeAt(0) - 65
  );
  return String.fromCodePoint(...codePoints);
}

// ============================================================================
// STATE ACCESSORS (Exported for tests and components)
// ============================================================================

export function getCountries(): SupportedCountryItem[] {
  return cache.countries;
}

export function isLoaded(): boolean {
  return cache.loadedAt !== null;
}

export function getCountryByCode(countryCode: string | null): SupportedCountryItem | undefined {
  if (!countryCode) return undefined;
  // Note: This linear search is OK because the list of supported countries is small (~20-30)
  // If it grows, we should build a lookup map in the cache.
  return cache.countries.find((c) => c.country_code.toUpperCase() === countryCode.toUpperCase());
}

// ============================================================================
// LOOKUP FUNCTIONS
// ============================================================================

/**
 * Get currency code for a country.
 * Falls back to USD if unknown.
 */
export function getCurrencyForCountry(countryCode: string | null): string {
  if (!countryCode) return 'USD';

  const code = countryCode.toUpperCase();

  // Try cache first
  const cached = cache.countryCurrencyMap.get(code);
  if (cached) return cached;

  // Fallback
  return FALLBACK_COUNTRY_CURRENCY[code] ?? 'USD';
}

/**
 * Get symbol for a currency.
 * Falls back to currency code if unknown.
 */
export function getSymbolForCurrency(currencyCode: string | null): string {
  if (!currencyCode) return '$';

  const code = currencyCode.toUpperCase();

  // Try cache first
  const cached = cache.currencySymbolMap.get(code);
  if (cached) return cached;

  // Fallback
  return FALLBACK_CURRENCY_SYMBOL[code] ?? code;
}

/**
 * Get flag emoji for a country.
 * Generates from Unicode if not in cache.
 */
export function getFlagForCountry(countryCode: string | null): string {
  if (!countryCode) return '🌍';

  const code = countryCode.toUpperCase();

  // Try cache first
  const cached = cache.countryFlagMap.get(code);
  if (cached) return cached;

  // Fallback - try to generate from Unicode
  const fallback = FALLBACK_COUNTRY_FLAG[code];
  if (fallback) return fallback;

  // Generate flag emoji from country code
  return countryCodeToFlag(code);
}

/**
 * Reset the cache for testing purposes.
 * @internal
 */
export function resetCountryDataCache(): void {
  cache.countries = [];
  cache.countryCurrencyMap.clear();
  cache.currencySymbolMap.clear();
  cache.countryFlagMap.clear();
  cache.loadedAt = null;
  fetchPromise = null;
}
